import './ICCSkillsPortfolioTemplate.scss';
import './PortfolioSpacing.scss';
import { API_BASE_URL } from '../../../../../utils/apiClient';
import DocumentLogo from '../../shared/DocumentLogo';
import DocumentPage from '../../shared/DocumentPage';
import EmployeePortrait from '../../shared/EmployeePortrait';
import TechnicalPattern from '../../shared/TechnicalPattern';
import { formatCertificationExpiry, formatExperienceRange, formatExperienceYears, formatSkillLevel } from '../../utils/documentFormatters';
import { portfolioPageTitle, resolvePortfolioLayout } from './resolvePortfolioLayout';

const firstPageByKind = (pages) => pages.reduce(
  (result, page) => ({ ...result, [page.kind]: result[page.kind] || page.pageNumber }),
  {}
);

const limitedText = (value = '', length = 260) => (
  value.length > length ? `${value.slice(0, length).trim()}…` : value
);

function PortfolioHeader({ title, data, sectionId }) {
  return (
    <header className="portfolio-header" id={sectionId}>
      <DocumentLogo />
      <div className="portfolio-header__title">
        <small>Dossier de compétences</small>
        <h1>{title}</h1>
      </div>
      <div className="portfolio-header__identity">
        <strong>{data.employee?.fullName}</strong>
        <span>{data.employee?.jobTitle}</span>
      </div>
    </header>
  );
}

function Section({ title, children }) {
  if (!children) return null;
  return <section className="portfolio-section"><h2>{title}</h2>{children}</section>;
}

function SkillsPage({ groups = [] }) {
  const tools = groups.filter(({ category }) => /logiciel|outil|cao|dao|bim/i.test(category));
  const domains = groups.filter(({ category }) => !/logiciel|outil|cao|dao|bim/i.test(category));
  return (
    <div className="portfolio-skills">
      {tools.length > 0 && (
        <Section title="Outils professionnels">
          <div className="portfolio-tools-grid">
            {tools.flatMap(({ items }) => items).slice(0, 6).map((item) => (
              <article key={item.id || item.name}>
                <span>{item.name?.slice(0, 2).toUpperCase()}</span>
                <div><strong>{item.name}</strong><small>{formatSkillLevel(item.level) || 'Pratique professionnelle'}</small></div>
              </article>
            ))}
          </div>
        </Section>
      )}
      <Section title="Domaines de compétences">
        <div className="portfolio-skills-grid">
          {domains.map(({ category, items }) => (
            <article className="portfolio-skill-group" key={category}>
              <h2>{category}</h2>
              <ul>{items.map((item) => (
                <li key={item.id || item.name}>
                  <strong>{item.name}</strong>
                  {item.level && <span>{formatSkillLevel(item.level)}</span>}
                </li>
              ))}</ul>
            </article>
          ))}
        </div>
      </Section>
    </div>
  );
}

function PrimaryExperiencesPage({ items = [] }) {
  return (
    <div className="portfolio-timeline">
      {items.map((item) => (
        <article className="portfolio-experience" key={item.id || `${item.jobTitle}-${item.startDate}`}>
          <time>{formatExperienceRange(item.startDate, item.endDate, item.isCurrent || item.currentPosition)}</time>
          <div>
            <span className="portfolio-experience__marker" />
            <h2>{item.jobTitle || item.title}</h2>
            <strong>{item.employer || item.employerName}</strong>
            {item.location && <small>{item.location}</small>}
            {item.summary && <p>{item.summary}</p>}
            {item.missions?.length > 0 && <ul>{item.missions.map((mission) => <li key={mission}>{mission}</li>)}</ul>}
          </div>
        </article>
      ))}
    </div>
  );
}

const portfolioReferenceClient = (item) => {
  const level = String(item.confidentialityLevel || item.confidentiality || '').trim().toLowerCase();
  const confidential = ['confidential', 'private', 'strictly_confidential', 'confidentiel', 'prive', 'privé'].includes(level);
  return confidential || (level !== 'public' && item.clientNameVisible === false) ? 'Client confidentiel' : item.client || item.clientName;
};

function ReferencesPage({ items = [] }) {
  const imageUrl = (item) => {
    const source = item.imageUrl || item.imagePath;
    if (!source || /^(?:data:|https?:)/i.test(source)) return source;
    return source.startsWith('/') ? `${API_BASE_URL}${source}` : source;
  };
  return (
    <div className={`portfolio-reference-grid portfolio-reference-grid--${items.length}`}>
      {items.map((item) => (
        <article className="portfolio-reference" key={item.id || item.projectName}>
          <div className="portfolio-reference__visual">
            {imageUrl(item) ? <img src={imageUrl(item)} alt="" /> : <span>ICC</span>}
          </div>
          <div className="portfolio-reference__body">
            <small>{[item.year || item.referenceYear, item.buildingType || item.projectType, item.location].filter(Boolean).join(' · ')}</small>
            <h2>{item.projectName}</h2>
            {(item.client || item.clientName) && <strong>{portfolioReferenceClient(item)}</strong>}
            {(item.mission || item.missionDescription) && <p>{limitedText(item.mission || item.missionDescription, 420)}</p>}
            <div className="portfolio-reference__tags">
              {(item.workPackages || item.lots || []).slice(0, 8).map((tag) => <span key={tag}>{tag}</span>)}
            </div>
            {(item.role || item.deliverables?.length > 0) && (
              <dl>
                {item.role && <><dt>Rôle</dt><dd>{item.role}</dd></>}
                {item.deliverables?.length > 0 && <><dt>Livrables</dt><dd>{item.deliverables.slice(0, 4).join(' · ')}</dd></>}
              </dl>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

function CredentialsPage({ education = [], trainings = [], certifications = [] }) {
  return (
    <div className="portfolio-credentials">
      {education.length > 0 && (
        <Section title="01 Parcours académique">
          <div className="portfolio-education-list">
            {education.map((item) => (
              <article className="portfolio-education" key={item.id || item.title}>
                <time>{item.year || item.graduationYear || '—'}</time>
                <div>
                  <strong>{item.title || item.diplomaName}</strong>
                  <span>{item.institution || item.institutionName}</span>
                  {(item.specialty || item.fieldOfStudy) && <small>{item.specialty || item.fieldOfStudy}</small>}
                </div>
              </article>
            ))}
          </div>
        </Section>
      )}
      {trainings.length > 0 && (
        <Section title="02 Formations professionnelles">
          <div className="portfolio-education-list">
            {trainings.map((item) => (
              <article className="portfolio-education" key={item.id || item.title}>
                <time>{String(item.endDate || item.startDate || '').slice(0, 4) || '—'}</time>
                <div>
                  <strong>{item.title}</strong>
                  <span>{[item.durationHours ? `${item.durationHours} heures` : item.durationDays ? `${item.durationDays} jours` : null, item.provider].filter(Boolean).join(' · ')}</span>
                  {item.skillsAcquired && <small>{item.skillsAcquired}</small>}
                </div>
              </article>
            ))}
          </div>
        </Section>
      )}
      {certifications.length > 0 && (
        <Section title="03 Habilitations et certifications">
          <div className="portfolio-certifications">
            {certifications.map((item) => {
              const expired = item.expiresAt && new Date(item.expiresAt) < new Date();
              return (
                <article className={`portfolio-certification ${expired ? 'portfolio-certification--expired' : ''}`} key={item.id || item.name}>
                  <span>{expired ? '!' : '✓'}</span>
                  <div>
                    <strong>{item.name}</strong>
                    <small>{item.noExpiration || item.hasNoExpiration ? 'Sans expiration' : item.expiresAt ? `${expired ? 'Expirée en' : 'Valable jusqu’en'} ${formatCertificationExpiry(item.expiresAt).toLowerCase()}` : 'Validité non renseignée'}</small>
                  </div>
                </article>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
}

function SummaryPage({ data, pages, options }) {
  const index = firstPageByKind(pages);
  const tocKinds = ['skills', 'primary-experiences', 'secondary-experiences', 'references', 'credentials'];
  const toc = tocKinds.filter((kind) => index[kind]);
  const missionCount = pages.filter((page) => page.kind === 'references').reduce((total, page) => total + page.items.length, 0);
  const softwareCount = data.tools?.length || (data.skills || []).filter((skill) => skill.itemKind ? skill.itemKind === 'tool' : /software|logiciel|cao|dao|bim/i.test(`${skill.category || ''} ${skill.expertiseDomain || ''}`)).length;
  const metrics = [
    { value: data.employee?.yearsOfExperience ? formatExperienceYears(data.employee.yearsOfExperience) : '0 an', label: 'd’expérience' },
    { value: missionCount, label: missionCount === 1 ? 'mission majeure' : 'missions majeures' },
    { value: softwareCount, label: softwareCount === 1 ? 'logiciel' : 'logiciels' },
    { value: data.certifications?.length || 0, label: (data.certifications?.length || 0) === 1 ? 'habilitation' : 'habilitations' },
  ];
  const expertise = (data.presentation?.mainFields || []).slice(0, 4);

  return (
    <div className="portfolio-summary-page">
      <div className="portfolio-summary-hero">
        {options.showPhoto !== false && <EmployeePortrait employee={data.employee} size="large" />}
        <div>
          <span>Profil professionnel</span>
          <h2>{data.employee?.fullName}</h2>
          <h3>{data.employee?.jobTitle}</h3>
          {data.employee?.shortTitle && <strong>{data.employee.shortTitle}</strong>}
          <p>{limitedText(data.presentation?.cvSummary || '', 620)}</p>
        </div>
      </div>
      <div className="portfolio-metrics">{metrics.map((item) => <div key={item.label}><strong>{item.value}</strong><span>{item.label}</span></div>)}</div>
      {expertise.length > 0 && <Section title="Domaines d’expertise"><div className="portfolio-expertise-grid">{expertise.map((field, i) => <article key={field}><span>0{i + 1}</span><strong>{field}</strong></article>)}</div></Section>}
      {toc.length > 0 && <Section title="Dans ce dossier"><ol className="portfolio-toc">{toc.map((kind, i) => <li key={kind}><a href={`#portfolio-section-${kind}`}><span>{String(i + 1).padStart(2, '0')}</span><strong>{portfolioPageTitle(kind)}</strong><small>p. {index[kind]}</small></a></li>)}</ol></Section>}
    </div>
  );
}

function Cover({ data }) {
  return (
    <>
      <TechnicalPattern />
      <div className="portfolio-cover">
        <div className="portfolio-cover__brand"><DocumentLogo inverse /></div>
        <div className="portfolio-cover__title"><small>Parcours professionnel</small><h1>Dossier<br />de compétences</h1><span /></div>
        <div className="portfolio-cover__identity">
          <h2>{data.employee?.fullName}</h2>
          <h3>{data.employee?.jobTitle}</h3>
          {data.presentation?.mainFields?.length > 0 && <div>{data.presentation.mainFields.slice(0, 5).map((field) => <span key={field}>{field}</span>)}</div>}
        </div>
        <small className="portfolio-cover__meta">Version {data.metadata?.templateVersion || 1} · {new Date(data.metadata?.generatedAt || Date.now()).toLocaleDateString('fr-FR')}</small>
      </div>
    </>
  );
}

export default function ICCSkillsPortfolioTemplate({ data = {}, options = {} }) {
  const pages = resolvePortfolioLayout(data, options);
  return (
    <div className="icc-skills-portfolio-template">
      {pages.map((page) => (
        <DocumentPage
          key={`${page.kind}-${page.pageNumber}`}
          pageNumber={page.pageNumber}
          totalPages={page.totalPages}
          documentType="Dossier de compétences"
          variant={page.kind === 'cover' ? 'portfolio-cover' : 'portfolio'}
          metadata={data.metadata}
          className={`portfolio-page portfolio-page--${page.kind}`}
        >
          {page.kind === 'cover' ? <Cover data={data} /> : (
            <>
              <PortfolioHeader title={portfolioPageTitle(page.kind)} data={data} sectionId={pages.find((item) => item.kind === page.kind) === page ? `portfolio-section-${page.kind}` : undefined} />
              {page.kind === 'summary' && <SummaryPage data={data} pages={pages} options={options} />}
              {page.kind === 'skills' && <SkillsPage groups={page.groups} />}
              {page.kind === 'primary-experiences' && <PrimaryExperiencesPage items={page.items} />}
              {page.kind === 'secondary-experiences' && <PrimaryExperiencesPage items={page.items} />}
              {page.kind === 'references' && <ReferencesPage items={page.items} />}
              {page.kind === 'credentials' && <CredentialsPage education={page.items || []} trainings={page.trainings || []} certifications={page.certifications || []} />}
            </>
          )}
        </DocumentPage>
      ))}
    </div>
  );
}
